import { AppConfig } from '@core/domain/AppConfig.ts';

export const MAX_FOTO_CELL_MM = 40;
export const MAX_FIRMA_CELL_MM = 32;
/** Max stacked height (mm) for guardian document images in the FO-GESR-2160 table. */
export const MAX_ACUDIENTE_DOC_STACK_MM = 11;
/** Total vertical budget (mm) for all guardian images in one participant row. */
export const MAX_ACUDIENTE_DOC_STACK_TOTAL_MM = 54;
/** Blob to data url. */


function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
    r.onerror = () => reject(r.error ?? new Error('readAsDataURL'));
    r.readAsDataURL(blob);
  });
}

/** Resolve data URL / blob / http(s) / same-origin path for embedding in the PDF. */
export async function resolveImageSrcForPdf(src: string): Promise<string | null> {
  const s = src.trim();
  if (!s) return null;
  if (s.startsWith('data:')) return s;
  try {
    if (s.startsWith('blob:')) {
      const res = await fetch(s);
      return await blobToDataUrl(await res.blob());
    }
    if (/^https?:\/\//i.test(s)) {
      if (AppConfig.useLocalSqliteClient()) return null;
      const res = await fetch(s);
      if (!res.ok) return null;
      return await blobToDataUrl(await res.blob());
    }
    if (globalThis.location !== undefined && (s.startsWith('participantes/') || s.startsWith('/'))) {
      const path = s.startsWith('/') ? s.slice(1) : s;
      const abs = new URL(path, globalThis.location.origin).href;
      const res = await fetch(abs);
      if (!res.ok) return null;
      return await blobToDataUrl(await res.blob());
    }
  } catch {
    return null;
  }
  return null;
}
/** Add image format from data url. */


export function addImageFormatFromDataUrl(dataUrl: string): 'PNG' | 'JPEG' | 'WEBP' {
  const fmtRe = /^data:image\/(png|jpeg|jpg|webp)/i;
  const m = fmtRe.exec(dataUrl);
  if (!m?.[1]) return 'PNG';
  const t = m[1].toLowerCase();
  if (t === 'jpeg' || t === 'jpg') return 'JPEG';
  if (t === 'webp') return 'WEBP';
  return 'PNG';
}

type GlobalWithCanvas = typeof globalThis & {
  document?: { createElement: (tag: string) => HTMLCanvasElement };
};

/** Typical mobile EXIF orientation fix, normalized to JPEG for `addImage`. */
export async function rasterDataUrlNormalizedForPdf(dataUrl: string): Promise<string> {
  const g = globalThis as GlobalWithCanvas;
  if (typeof createImageBitmap !== 'function' || g.document?.createElement == null) {
    return dataUrl;
  }
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    const canvas = g.document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bmp.close?.();
      return dataUrl;
    }
    ctx.drawImage(bmp, 0, 0);
    bmp.close?.();
    const out = canvas.toDataURL('image/jpeg', 0.92);
    return out.startsWith('data:image') ? out : dataUrl;
  } catch {
    return dataUrl;
  }
}
/** Natural image size. */


export async function naturalImageSize(dataUrl: string): Promise<{ w: number; h: number }> {
  const Img = globalThis.Image as typeof Image | undefined;
  if (!Img) {
    return { w: 100, h: 100 };
  }
  return new Promise((resolve, reject) => {
    const img = new Img();
    img.onload = () => resolve({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    img.onerror = () => reject(new Error('imagen'));
    img.src = dataUrl;
  });
}
/** Fit in box. */


export function fitInBox(nw: number, nh: number, maxW: number, maxH: number): { dw: number; dh: number } {
  const r = Math.min(maxW / nw, maxH / nh, 1);
  return { dw: nw * r, dh: nh * r };
}

const OSM_RASTER_ZOOM = 16;
const OSM_TILE_PX = 256;
/** Lon to tile f. */


function lonToTileF(lon: number, zoom: number): number {
  return ((lon + 180) / 360) * 2 ** zoom;
}
/** Lat to tile f. */


function latToTileF(lat: number, zoom: number): number {
  const latRad = (lat * Math.PI) / 180;
  return (
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * 2 ** zoom
  );
}
/** Loads tile as image. */


function loadTileAsImage(z: number, tx: number, ty: number): Promise<HTMLImageElement> {
  const url = `https://tile.openstreetmap.org/${z}/${tx}/${ty}.png`;
  const Img = globalThis.Image as typeof Image | undefined;
  if (!Img) return Promise.reject(new Error('no Image'));
  return new Promise((resolve, reject) => {
    const img = new Img();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('tile'));
    img.src = url;
  });
}

type OsmTilePlacement = { i: number; j: number; tx: number; ty: number };
/** Builds osm tile placements. */


function buildOsmTilePlacements(tx0: number, ty0: number, n: number): OsmTilePlacement[] | null {
  const place: OsmTilePlacement[] = [];
  for (let j = 0; j < 3; j += 1) {
    for (let i = 0; i < 3; i += 1) {
      const tx = (tx0 + i + n) % n;
      const myTy = ty0 + j;
      if (myTy < 0 || myTy > n - 1) return null;
      place.push({ i, j, tx, ty: myTy });
    }
  }
  return place;
}

/**
 * 2:1 raster from the OSM Mapnik tile grid (same basemap as the form iframe),
 * with a marker at the coordinates. Browser CORS: osm.org tiles are usually more reliable
 * than a PHP staticmap proxy.
 */
async function tryOsmRasterTilesMapDataUrl(lat: number, lon: number): Promise<string | null> {
  const z = OSM_RASTER_ZOOM;
  const xf = lonToTileF(lon, z);
  const yf = latToTileF(lat, z);
  const n = 2 ** z;
  if (!Number.isFinite(xf) || !Number.isFinite(yf)) return null;
  if (xf < 0 || yf < 0 || xf > n || yf > n) return null;

  const tx0 = Math.floor(xf) - 1;
  const ty0 = Math.floor(yf) - 1;
  const w = 3 * OSM_TILE_PX;
  const h = 3 * OSM_TILE_PX;
  const px = (xf - tx0) * OSM_TILE_PX;
  const py = (yf - ty0) * OSM_TILE_PX;

  const c = globalThis.document?.createElement('canvas');
  if (!c) return null;
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return null;

  const place = buildOsmTilePlacements(tx0, ty0, n);
  if (!place) return null;
  let tiles: Awaited<ReturnType<typeof loadTileAsImage>>[];
  try {
    tiles = await Promise.all(place.map((p) => loadTileAsImage(z, p.tx, p.ty)));
  } catch {
    return null;
  }
  for (const [k, p] of place.entries()) {
    const img = tiles[k];
    if (img === undefined) continue;
    ctx.drawImage(img, p.i * OSM_TILE_PX, p.j * OSM_TILE_PX);
  }

  const r = 7;
  ctx.beginPath();
  ctx.arc(px, py, r, 0, 2 * Math.PI);
  ctx.fillStyle = '#c62828';
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(px, py + r);
  ctx.lineTo(px, py + r + 4);
  ctx.stroke();

  const outH2 = Math.min(Math.floor(w / 2), h);
  const yCrop = Math.max(0, Math.min(h - outH2, Math.round(py - outH2 / 2)));
  const out = globalThis.document.createElement('canvas');
  out.width = w;
  out.height = outH2;
  const octx = out.getContext('2d');
  if (!octx) return null;
  octx.drawImage(c, 0, yCrop, w, outH2, 0, 0, w, outH2);

  try {
    return out.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Raster map image (OpenStreetMap / mapnik) from coordinates. Tries official tiles first;
 * falls back to staticmap (e.g. environments without canvas / DOM).
 */
export async function fetchStaticMapDataUrl(latStr: string, lonStr: string): Promise<string | null> {
  const lat = Number(String(latStr).trim().replace(',', '.'));
  const lon = Number(String(lonStr).trim().replace(',', '.'));
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;

  const fromTiles = await tryOsmRasterTilesMapDataUrl(lat, lon);
  if (fromTiles) return fromTiles;

  const url = `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lon}&zoom=16&size=1024x512&markers=${lat},${lon},red-pushpin`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}
